/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CreditCard, Check, Loader2, ExternalLink, Sparkles, AlertTriangle, Lock, Stethoscope, CheckCircle2, XCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { getAiBillingPlans, startAiPlanCheckout, getAiCheckoutStatus, openAiBillingPortal, formatEur, runStripeDiagnostics } from '@/lib/aiBilling';
import { formatCompact } from '@/lib/aiUsage';

const TIER_RANK = { Entry: 0, Professional: 1, Enterprise: 2 };

/**
 * AiPlanUpgradeCard — upgrade self-service del piano AI (solo admin clinica).
 * - Stripe non configurato: card informativa con le variabili mancanti (nessun pulsante attivo).
 * - Ritorno da Stripe (?billing=success&session_id=): polling stato → toast + onChanged().
 */
export const AiPlanUpgradeCard = ({ onChanged }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null); // tier in checkout | 'portal'
  const [params, setParams] = useSearchParams();
  const polled = useRef(false);
  const [diag, setDiag] = useState(null);
  const [diagBusy, setDiagBusy] = useState(false);

  const diagnose = async () => {
    setDiagBusy(true);
    try {
      const d = await runStripeDiagnostics('platform');
      setDiag(d);
      if (d.stripe_status === 'authenticated' && d.webhooks_configured) toast.success('Stripe piattaforma: autenticato e webhook registrato.');
      else toast.warning('Diagnostica completata: vedi le azioni consigliate.');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Diagnostica non riuscita'); } finally { setDiagBusy(false); }
  };

  const load = useCallback(async () => {
    try { setData(await getAiBillingPlans()); setError(null); } catch (e) { setError(e?.response?.data?.detail || 'Impossibile caricare i piani'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Ritorno da Stripe Checkout
  useEffect(() => {
    const billing = params.get('billing');
    const sid = params.get('session_id');
    if (!billing || polled.current) return;
    polled.current = true;
    const clear = () => { const p = new URLSearchParams(params); p.delete('billing'); p.delete('session_id'); setParams(p, { replace: true }); };
    if (billing === 'cancel') { toast.info('Pagamento annullato: il piano non è cambiato.'); clear(); return; }
    if (billing !== 'success' || !sid) { clear(); return; }
    let attempts = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const s = await getAiCheckoutStatus(sid);
        if (s.payment_status === 'paid') {
          toast.success(`Piano ${s.plan?.plan_tier} attivo: ${formatCompact(s.plan?.max_monthly_requests)} richieste AI al mese da subito.`, { duration: 8000 });
          clear(); load(); onChanged?.(); return;
        }
        if (['failed', 'expired'].includes(s.payment_status)) { toast.error('Pagamento non riuscito. Riprova o contatta il supporto.'); clear(); return; }
      } catch { /* retry */ }
      if (attempts < 8) setTimeout(tick, 2000); else { toast.warning('Conferma del pagamento in corso: il piano verrà aggiornato automaticamente appena Stripe notifica il pagamento.'); clear(); }
    };
    tick();
  }, [params, setParams, load, onChanged]);

  const upgrade = async (tier) => {
    setBusy(tier);
    try {
      const { checkout_url } = await startAiPlanCheckout(tier);
      window.location.assign(checkout_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Impossibile avviare il pagamento');
      setBusy(null);
    }
  };
  const portal = async () => {
    setBusy('portal');
    try { const { portal_url } = await openAiBillingPortal(); window.location.assign(portal_url); } catch (e) { toast.error(e?.response?.data?.detail || 'Portale non disponibile'); setBusy(null); }
  };

  if (error) return null;
  if (!data) return <Skeleton className="h-56 rounded-xl" />;

  const current = data.current_tier || 'Entry';
  const active = ['active', 'trialing'].includes(data.subscription_status);

  return (
    <Card className="rounded-xl" id="upgrade" data-testid="ai-plan-upgrade-card">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> Piano AI di questa clinica</CardTitle>
            <CardDescription>Abbonamento mensile ricorrente: il limite si alza subito dopo il pagamento e torna Entry se l'abbonamento viene cancellato o non pagato. Prezzi IVA inclusa; in fattura vengono raccolti indirizzo e P.IVA.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[10px]" data-testid="ai-plan-current">Attuale: {current}{active ? ' · abbonamento attivo' : ''}</Badge>
            {data.has_customer && data.configured && (
              <Button variant="ghost" size="sm" className="h-8 rounded-lg" onClick={portal} disabled={!!busy} data-testid="ai-plan-portal-btn">
                {busy === 'portal' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Gestisci abbonamento
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {!data.configured && (
          <div className="rounded-xl border border-warning/50 bg-warning/10 p-3 text-xs flex items-start gap-2" data-testid="ai-plan-not-configured">
            <AlertTriangle className="h-4 w-4 text-warning mt-0.5 flex-shrink-0" />
            <div>
              <b>Stripe piattaforma non configurato.</b> Per attivare i pagamenti self-service inserisci in <code className="font-mono">backend/.env</code>: {data.missing.map((m) => <code key={m} className="font-mono mx-1">{m}</code>)}
              e registra il webhook su <code className="font-mono">{window.location.origin}{data.webhook_path}</code> (eventi: checkout.session.completed, customer.subscription.updated/deleted, invoice.paid, invoice.payment_failed).
              Nel frattempo puoi cambiare il tier manualmente dalla tabella qui sopra.
            </div>
          </div>
        )}
        <div className="grid gap-3 sm:grid-cols-3">
          {data.tiers.map((t) => {
            const isCurrent = t.tier === current;
            const isUpgrade = TIER_RANK[t.tier] > TIER_RANK[current];
            return (
              <div key={t.tier} className={cn('rounded-xl border p-4 flex flex-col gap-2', isCurrent ? 'border-primary bg-primary/5' : 'border-border/70')} data-testid={`ai-plan-tier-${t.tier}`}>
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold">{t.tier}</div>
                  {isCurrent && <Badge className="text-[10px]"><Check className="h-3 w-3 mr-1" /> attuale</Badge>}
                </div>
                <div className="text-2xl font-display font-bold tabular-nums">{t.price_eur ? formatEur(t.price_eur) : 'Incluso'}<span className="text-xs font-normal text-muted-foreground">{t.price_eur ? ' / mese' : ''}</span></div>
                <div className="text-xs text-muted-foreground">{formatCompact(t.max_monthly_requests)} richieste AI / mese</div>
                <div className="text-[11px] text-muted-foreground flex-1">{t.blurb}</div>
                {t.price_eur > 0 && (
                  <Button size="sm" className="rounded-xl h-10 mt-1" variant={isUpgrade ? 'default' : 'outline'} disabled={!data.configured || !!busy || isCurrent} onClick={() => upgrade(t.tier)} data-testid={`ai-plan-upgrade-${t.tier}`}>
                    {busy === t.tier ? <Loader2 className="h-4 w-4 animate-spin" /> : !data.configured ? <Lock className="h-4 w-4" /> : <CreditCard className="h-4 w-4" />}
                    {isCurrent ? 'Piano attivo' : isUpgrade ? `Passa a ${t.tier}` : `Scegli ${t.tier}`}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
        <div className="rounded-xl border border-border/70 p-3 space-y-2" data-testid="stripe-diagnostics-box">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs"><b>Verifica configurazione Stripe</b> <span className="text-muted-foreground">— chiavi, connessione API, webhook. Nessun segreto viene mostrato.</span></div>
            <Button variant="outline" size="sm" className="h-8 rounded-lg" onClick={diagnose} disabled={diagBusy} data-testid="stripe-diagnostics-run">
              {diagBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Stethoscope className="h-4 w-4" />} Esegui diagnostica
            </Button>
          </div>
          {diag && (
            <div className="space-y-1.5" data-testid="stripe-diagnostics-result">
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="outline" className={cn('text-[10px] gap-1', diag.stripe_status === 'authenticated' ? 'border-success/50 text-success' : 'border-destructive/50 text-destructive')}>{diag.stripe_status === 'authenticated' ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />} API: {diag.stripe_status}</Badge>
                <Badge variant="outline" className="text-[10px]">Ambiente: {diag.environment}</Badge>
                <Badge variant="outline" className={cn('text-[10px] gap-1', diag.webhooks_configured ? 'border-success/50 text-success' : 'border-warning/50 text-warning')}>{diag.webhooks_configured ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />} Webhook {diag.webhooks_configured ? 'ok' : 'da configurare'}</Badge>
                <Badge variant="outline" className={cn('text-[10px] gap-1', diag.secrets_hardcoded_in_source ? 'border-destructive/50 text-destructive' : 'border-success/50 text-success')}>{diag.secrets_hardcoded_in_source ? <XCircle className="h-3 w-3" /> : <CheckCircle2 className="h-3 w-3" />} Nessuna chiave nel codice</Badge>
              </div>
              {(diag.scopes?.platform?.actions || []).length > 0 && (
                <ul className="text-[11px] space-y-1 pl-4 list-disc text-muted-foreground">
                  {diag.scopes.platform.actions.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Lock className="h-3 w-3" /> Pagamento sicuro su Stripe{data.live_mode === false && data.configured ? ' (modalità test)' : ''}. Il contatore del ciclo corrente non viene azzerato: cambia solo il limite.</p>
      </CardContent>
    </Card>
  );
};

export default AiPlanUpgradeCard;
